// Mock data for AI Receptionist Landing Page
export const mockData = {
  company: {
    name: "Ghost Engineering",
    product: "AI Receptionist",
    website: "https://www.ghostengr.com/",
    logo: "GE"
  },
  
  features: [
    {
      id: 1,
      title: "Trained Specifically to Your Business",
      description: "Our AI learns your business inside and out, providing personalized responses that sound authentically you.",
      icon: "Users"
    },
    {
      id: 2,
      title: "Warm Call Transfers",
      description: "Seamlessly transfer calls to real people when needed with complete context and warm handoffs.",
      icon: "Phone"
    },
    {
      id: 3,
      title: "Lead Capture Excellence",
      description: "Never miss a customer's call again. We get you leads fast with intelligent qualification and routing.",
      icon: "Zap"
    },
    {
      id: 4,
      title: "Full Appointment Scheduling",
      description: "Complete scheduling capability integrated with your existing calendar systems.",
      icon: "Calendar"
    },
    {
      id: 5,
      title: "Knowledge Base Integration",
      description: "Feed existing knowledge bases or create new ones tailored to your business needs.",
      icon: "Database"
    },
    {
      id: 6,
      title: "Multilingual Support",
      description: "Serve customers in their preferred language with natural conversation flow.",
      icon: "Languages"
    },
    {
      id: 7,
      title: "Customizable Voice & Tone",
      description: "Choose from different voices and tones that match your brand personality.",
      icon: "Mic"
    },
    {
      id: 8,
      title: "Post-Call Analysis",
      description: "Detailed summaries and data routable to any CMS for comprehensive insights.",
      icon: "BarChart3"
    },
    {
      id: 9,
      title: "Spam Filtering",
      description: "Protect your team from unnecessary calls and mitigate spam risks automatically.",
      icon: "Shield"
    },
    {
      id: 10,
      title: "Custom Hold Music",
      description: "Personalize the caller experience with your choice of hold music and messaging.",
      icon: "Music"
    },
    {
      id: 11,
      title: "24/7 Availability",
      description: "Always-on professional presence that never sleeps, never takes a break.",
      icon: "Clock"
    },
    {
      id: 12,
      title: "Smart Automation",
      description: "Custom triggers and automated workflows to notify your team about leads and calls.",
      icon: "MessageCircle"
    },
    {
      id: 13,
      title: "High Capacity Handling",
      description: "Handle up to 20 simultaneous calls without missing a beat.",
      icon: "Users"
    }
  ],

  pricing: {
    starter: {
      name: "Starter",
      price: "$1.00",
      period: "per minute",
      monthlyFee: "Pay-as-you-go (no monthly minimum)",
      savings: "",
      features: [
        "AI Receptionist Call Answering",
        "Call Transcription (text)",
        "Basic Call Screening & Forwarding"
      ],
      idealFor: "Small businesses, startups, or those looking to try our advanced voice agent and upgrade later.",
      popular: false
    },
    professional: {
      name: "Professional",
      price: "$0.85",
      period: "per minute",
      monthlyFee: "Minimum 500 minutes/month (~$425/month)",
      savings: "15% off Starter rate",
      features: [
        "Everything in Starter, plus:",
        "Priority Call Routing (Instant transfer)",
        "Custom Call Scripts & Dialog Flows",
        "Advanced Caller Analytics Dashboard",
        "Voicemail-to-Email integration"
      ],
      idealFor: "Growing businesses or busy professionals needing advanced customization and analytics.",
      popular: true
    },
    business: {
      name: "Business",
      price: "$0.70",
      period: "per minute",
      monthlyFee: "Minimum 1,500 minutes/month (~$1,050/month)",
      savings: "30% off Starter rate",
      features: [
        "Everything in Professional, plus:",
        "Dedicated Support Specialist",
        "Integration with CRM (Salesforce, HubSpot, Zoho)",
        "2 Custom API/Webhook Integrations",
        "Multilingual Support (English/Spanish)",
        "Smart Scheduling & Appointment Setting Automation"
      ],
      idealFor: "Medium to large businesses needing automation, deep integrations, and personalized support.",
      popular: false
    },
    enterprise: {
      name: "Enterprise",
      price: "Custom Pricing",
      period: "5,000+ minutes/month",
      monthlyFee: "Customized based on volume (minimum 5,000 minutes)",
      savings: "Significant discounts for high volume",
      features: [
        "Everything in Business, plus:",
        "Dedicated Account Manager",
        "Custom AI Model Training & Optimization",
        "White-glove Onboarding and Training",
        "SLA (Service Level Agreement) with Guaranteed Uptime",
        "Custom Integrations & Workflow Development"
      ],
      idealFor: "Enterprises or large organizations needing extensive call handling, deep customization, and dedicated enterprise-grade service.",
      popular: false
    }
  },

  testimonials: [
    {
      id: 1,
      name: "Sarah Johnson",
      title: "Solo Marketing Consultant",
      content: "Ghost Engineering's AI Receptionist has transformed how I handle client calls. I never miss an opportunity now, and the lead qualification is spot-on.",
      rating: 5
    },
    {
      id: 2,
      name: "Dr. Michael Chen",
      title: "Medical Practice Owner",
      content: "The 24/7 availability and appointment scheduling have been game-changers for our practice. Patients love the seamless experience.",
      rating: 5
    },
    {
      id: 3,
      name: "Lisa Rodriguez",
      title: "HVAC Service Company",
      content: "Emergency calls are handled perfectly, and the warm transfers to our technicians are flawless. Our response time has improved dramatically.",
      rating: 5
    }
  ],

  sampleCalls: [
    {
      id: 1,
      businessType: "Law Office",
      scenario: "New Client Consultation Inquiry",
      duration: "2:34",
      audioUrl: "/samples/law-office-consultation.mp3", // Will be added when recordings are ready
      transcript: [
        { speaker: "AI", text: "Good morning, thank you for calling Smith & Associates Law Firm. This is Sarah, how may I assist you today?" },
        { speaker: "Caller", text: "Hi, I was in a car accident yesterday and I think I need a lawyer." },
        { speaker: "AI", text: "I'm sorry to hear about your accident. I'd be happy to help you get connected with one of our personal injury attorneys. Can I get your name and phone number first?" },
        { speaker: "Caller", text: "Sure, it's John Miller, and my number is 555-0123." },
        { speaker: "AI", text: "Thank you John. I have you down for a consultation. Are you available this Thursday at 2 PM, or would Friday at 10 AM work better for you?" },
        { speaker: "Caller", text: "Thursday at 2 PM works perfectly." },
        { speaker: "AI", text: "Perfect! I've scheduled your consultation for Thursday at 2 PM. You'll receive a confirmation email shortly with all the details and some forms to fill out beforehand." }
      ]
    },
    {
      id: 2,
      businessType: "Medical Practice",
      scenario: "Appointment Scheduling",
      duration: "1:47",
      audioUrl: "/samples/medical-appointment.mp3",
      transcript: [
        { speaker: "AI", text: "Good afternoon, Wellness Medical Center. This is Emma, how can I help you today?" },
        { speaker: "Caller", text: "Hi, I need to schedule an appointment for my annual physical." },
        { speaker: "AI", text: "I'd be happy to help you schedule that. May I have your name and date of birth?" },
        { speaker: "Caller", text: "Jennifer Davis, March 15th, 1985." },
        { speaker: "AI", text: "Thank you Jennifer. I see you're an existing patient. Dr. Rodriguez has availability next Tuesday at 9 AM or Wednesday at 3 PM. Which works better for you?" },
        { speaker: "Caller", text: "Tuesday at 9 AM would be great." },
        { speaker: "AI", text: "Perfect! I've scheduled your annual physical for Tuesday at 9 AM. Please remember to fast for 12 hours before your appointment for your lab work." }
      ]
    },
    {
      id: 3,
      businessType: "HVAC Service",
      scenario: "Emergency Call Handling",
      duration: "1:23",
      audioUrl: "/samples/hvac-emergency.mp3",
      transcript: [
        { speaker: "AI", text: "Climate Control Solutions, this is Alex. Are you calling about an emergency?" },
        { speaker: "Caller", text: "Yes! My heating system just died and it's freezing in here. I have a newborn baby." },
        { speaker: "AI", text: "I understand this is urgent, especially with a newborn. Let me get your address and I'll dispatch our emergency technician right away." },
        { speaker: "Caller", text: "It's 123 Oak Street, apartment 4B." },
        { speaker: "AI", text: "Got it. I have Mike heading to you now - he'll be there in 20 minutes. He's our most experienced technician and has all the parts for emergency repairs. Is this the best number to reach you?" },
        { speaker: "Caller", text: "Yes, thank you so much!" },
        { speaker: "AI", text: "You're welcome! Mike will call you when he's 5 minutes away. Stay warm!" }
      ]
    },
    {
      id: 4,
      businessType: "Marketing Consultant",
      scenario: "Lead Qualification",
      duration: "3:12",
      audioUrl: "/samples/marketing-lead.mp3",
      transcript: [
        { speaker: "AI", text: "Miller Marketing Solutions, this is Jessica. How can I help you today?" },
        { speaker: "Caller", text: "Hi, I'm looking for help with digital marketing for my restaurant." },
        { speaker: "AI", text: "I'd love to help with that! Digital marketing is perfect for restaurants. Can I get your name and the name of your restaurant?" },
        { speaker: "Caller", text: "I'm Tony, and it's Tony's Italian Bistro downtown." },
        { speaker: "AI", text: "Nice to meet you Tony. What's your biggest challenge right now - is it getting more customers, online reviews, or social media presence?" },
        { speaker: "Caller", text: "Mainly getting more customers. Our location is great but people don't know we exist." },
        { speaker: "AI", text: "That's exactly what we specialize in. Lisa, our restaurant marketing expert, would love to chat with you. She has a free 30-minute strategy session available tomorrow at 2 PM. Would that work for you?" }
      ]
    },
    {
      id: 5,
      businessType: "Property Management",
      scenario: "Maintenance Request",
      duration: "2:01",
      audioUrl: "/samples/property-maintenance.mp3",
      transcript: [
        { speaker: "AI", text: "Metro Property Management, this is David. How can I assist you?" },
        { speaker: "Caller", text: "Hi, I'm a tenant at Riverside Apartments and my kitchen faucet is leaking really bad." },
        { speaker: "AI", text: "I can help with that maintenance request. Can I get your name and apartment number?" },
        { speaker: "Caller", text: "Sarah Johnson, apartment 2A." },
        { speaker: "AI", text: "Thank you Sarah. I see you're in building 2. Is this preventing you from using your kitchen normally?" },
        { speaker: "Caller", text: "Yes, it's dripping constantly and I can't turn it off completely." },
        { speaker: "AI", text: "I've marked this as urgent. Our maintenance team will be there today between 2 and 4 PM. I'll send you a text confirmation with the exact time window." }
      ]
    },
    {
      id: 6,
      businessType: "Salon & Spa",
      scenario: "Appointment Booking",
      duration: "2:28",
      audioUrl: "/samples/salon-booking.mp3",
      transcript: [
        { speaker: "AI", text: "Serenity Salon and Spa, this is Maya. How can I pamper you today?" },
        { speaker: "Caller", text: "Hi, I'd like to book a massage and maybe a facial for this weekend." },
        { speaker: "AI", text: "That sounds wonderful! Are you looking for a relaxing Swedish massage or something more therapeutic?" },
        { speaker: "Caller", text: "Swedish massage would be perfect, and a hydrating facial." },
        { speaker: "AI", text: "Great choice! I can do both treatments back-to-back. We have Saturday at 2 PM available, which would give you the full afternoon to relax. The massage is 60 minutes and the facial is 75 minutes." },
        { speaker: "Caller", text: "That sounds perfect. How much will that be?" },
        { speaker: "AI", text: "The total is $185, and I can take a card to hold your appointment if you'd like." }
      ]
    }
  ],

  useCases: [
    {
      id: 1,
      title: "Law Offices",
      description: "Professional intake, appointment booking, and lead qualification for legal practices."
    },
    {
      id: 2,
      title: "Medical & Wellness",
      description: "HIPAA-compliant call handling, appointment scheduling, and patient communication."
    },
    {
      id: 3,
      title: "Home Services",
      description: "24/7 emergency support, intelligent routing, and service scheduling for HVAC, plumbing, electrical."
    },
    {
      id: 4,
      title: "Agencies & Consultants",
      description: "Professional presence, lead qualification, and seamless client communication."
    },
    {
      id: 5,
      title: "Property Management",
      description: "Tenant inquiries, maintenance requests, and property showing coordination."
    },
    {
      id: 6,
      title: "Salons & Spas",
      description: "Appointment booking, service inquiries, and customer support automation."
    }
  ],

  stats: {
    availability: "24/7",
    simultaneousCalls: "20+",
    leadCaptureRate: "100%"
  },

  ctaButtons: {
    primary: "Book a Demo",
    secondary: "View Features"
  }
};

export default mockData;